import { gql } from "@apollo/client";
import { EVENT_CHECK_IN_PROVENANCE_FRAGMENT } from "../check-in-provenance-gql";


export const STAFF_MANAGEMENT_WORK_SESSION_LOG_SNAPSHOT_FRAGMENT = gql`
    fragment StaffManagementWorkSessionLogSnapshot on WorkSessionLog {
        id
        action
        status
        reason
        detail
        latitude
        longitude
        accuracy
        zone_latitude
        zone_longitude
        zone_radius
        getDistanceFromZone
        device_uuid
        platform
        ip
        user_agent
        created_at
    }
`;

export const GET_STAFF_MANAGEMENT_EVENT = gql`
    ${ EVENT_CHECK_IN_PROVENANCE_FRAGMENT }

    query StaffManagementExhibitionEvent($id: ID) {
        exhibitionEvent(id: $id) {
            id
            title
            check_latitude
            check_longitude
            check_radius
            check_country
            check_address
            getCheckQrCodeUrl
            check_qr_generated_at
            ...EventCheckInProvenance
            getWorkerRolesCount
            relWorkerSubcontractors {
                id
                title
            }
            relWorkerRoles {
                id
                label
                colorHex
                getExhibitionWorkersCount(ee_id: $id)
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_CHECK_IN_HISTORY = gql`
    query StaffManagementCheckInHistory(
        $where: QueryExhibitionEventCheckinLogsWhereWhereConditions
        $orderBy: [QueryExhibitionEventCheckinLogsOrderByOrderByClause!]
        $first: Int!
        $page: Int
    ) {
        exhibitionEventCheckinLogs(
            where: $where
            orderBy: $orderBy
            first: $first
            page: $page
        ) {
            paginatorInfo {
                total
                currentPage
                perPage
                lastPage
                hasMorePages
            }
            data {
                id
                ee_id
                user_id
                source
                created_at
                changedFields
                old_latitude
                old_longitude
                old_radius
                old_country
                old_address
                new_latitude
                new_longitude
                new_radius
                new_country
                new_address
                relUser {
                    id
                    name
                    surname
                }
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_ROLES = gql`
    query StaffManagementRoles(
        $eeID: ID!
        $text: String
        $first: Int
        $page: Int
        $orderBy: [QueryRolesOrderByOrderByClause!]
        $where: QueryRolesWhereWhereConditions
    ) {
        roles(
            text: $text
            first: $first
            page: $page
            orderBy: $orderBy
            where: $where
        ) {
            paginatorInfo {
                total
                count
                currentPage
                perPage
                lastPage
            }
            data {
                id
                label
                slug
                colorHex
                getExhibitionWorkersCount(ee_id: $eeID)
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_EVENTS = gql`
    query StaffManagementExhibitionEvents(
        $text: String
        $computedStatus: EeComputedStatusEnum
        $where: QueryExhibitionEventsWhereWhereConditions
        $orderBy: [QueryExhibitionEventsOrderByOrderByClause!]
        $first: Int
        $page: Int
    ) {
        exhibitionEvents(
            text: $text
            computedStatus: $computedStatus
            where: $where
            orderBy: $orderBy
            first: $first
            page: $page
        ) {
            paginatorInfo {
                total
                count
                currentPage
                perPage
                lastPage
            }
            data {
                id
                title
                start_at
                end_at
                dismantling_start_at
                dismantling_end_at
                constructionStart_at
                constructionEnd_at
                check_latitude
                check_longitude
                check_radius
                getCheckQrCodeUrl
                check_qr_generated_at
                getComputedStatus
                getWorkerUsersCount
                getWorkerSessionActiveCount
                getWorkerSubcontractorsCount
                relWorkerSubcontractors {
                    id
                    title
                }
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_WORKERS = gql`
    query StaffManagementWorkers(
        $eeID: [ID!]!
        $text: String
        $where: QueryUsersWhereWhereConditions
        $orderBy: [QueryUsersOrderByOrderByClause!]
        $first: Int!
        $page: Int
    ) {
        users(
            ee_id: $eeID
            workersOnly: true
            text: $text
            where: $where
            orderBy: $orderBy
            first: $first
            page: $page
        ) {
            paginatorInfo {
                total
                count
                currentPage
                perPage
                lastPage
            }
            data {
                id
                name
                surname
                phone
                email
                role_id
                role {
                    id
                    label
                    colorHex
                }
                subcontractor_id
                subcontractor {
                    id
                    title
                }
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_WORKER_SESSIONS = gql`
    query StaffManagementWorkerSessions(
        $where: QueryWorkSessionsWhereWhereConditions
        $orderBy: [QueryWorkSessionsOrderByOrderByClause!]
        $first: Int!
        $page: Int
    ) {
        workSessions(
            where: $where
            orderBy: $orderBy
            first: $first
            page: $page
        ) {
            paginatorInfo {
                currentPage
                lastPage
                hasMorePages
            }
            data {
                id
                user_id
                check_out_at
                getWorkedHours
                getCheckOutStatus
            }
        }
    }
`;

// Temporary API workaround: the date resolver returns YYYY-MM-DD while its
// published output type is DateTime. The group id contains the same date key.
export const GET_STAFF_MANAGEMENT_ATTENDANCE_BY_DATE = gql`
    ${ STAFF_MANAGEMENT_WORK_SESSION_LOG_SNAPSHOT_FRAGMENT }

    query StaffManagementAttendanceByDate(
        $eeID: ID!
        $text: String
        $dateFrom: Date
        $dateTo: Date
        $subcontractorID: ID
    ) {
        workSessionGroupByDate(
            ee_id: $eeID
            text: $text
            date_from: $dateFrom
            date_to: $dateTo
            subcontractor_id: $subcontractorID
        ) {
            id
            date: id
            workersCount
            totalHours
            workSessions {
                id
                user_id
                check_in_at
                check_out_at
                getWorkedHours
                getCheckOutStatus
                relCheckInLog {
                    ...StaffManagementWorkSessionLogSnapshot
                }
                relCheckOutLog {
                    ...StaffManagementWorkSessionLogSnapshot
                }
                getFailedCheckoutSummary {
                    attempts_count
                    first_attempt_at
                    last_attempt_at
                    last_distance_from_zone
                    last_failed_log {
                        ...StaffManagementWorkSessionLogSnapshot
                    }
                }
                relUser {
                    id
                    name
                    surname
                    subcontractor_id
                    subcontractor {
                        id
                        title
                    }
                }
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_ATTENDANCE_BY_SUBCONTRACTOR = gql`
    ${ STAFF_MANAGEMENT_WORK_SESSION_LOG_SNAPSHOT_FRAGMENT }

    query StaffManagementAttendanceBySubcontractor(
        $eeID: ID!
        $text: String
        $dateFrom: Date
        $dateTo: Date
        $subcontractorID: ID
    ) {
        workSessionGroupBySubcontractor(
            ee_id: $eeID
            text: $text
            date_from: $dateFrom
            date_to: $dateTo
            subcontractor_id: $subcontractorID
        ) {
            id
            subcontractor {
                id
                title
            }
            workersCount
            totalHours
            workSessions {
                id
                user_id
                check_in_at
                check_out_at
                getWorkedHours
                getCheckOutStatus
                relCheckInLog {
                    ...StaffManagementWorkSessionLogSnapshot
                }
                relCheckOutLog {
                    ...StaffManagementWorkSessionLogSnapshot
                }
                getFailedCheckoutSummary {
                    attempts_count
                    first_attempt_at
                    last_attempt_at
                    last_distance_from_zone
                    last_failed_log {
                        ...StaffManagementWorkSessionLogSnapshot
                    }
                }
                relUser {
                    id
                    name
                    surname
                    subcontractor_id
                    subcontractor {
                        id
                        title
                    }
                }
            }
        }
    }
`;

export const GET_STAFF_MANAGEMENT_AUDIT_LOGS = gql`
    ${ STAFF_MANAGEMENT_WORK_SESSION_LOG_SNAPSHOT_FRAGMENT }

    query StaffManagementAuditLogs(
        $text: String
        $result: WorkSessionLogResultEnum
        $where: QueryWorkSessionLogsWhereWhereConditions
        $whereUser: QueryWorkSessionLogsWhereUserWhereHasConditions
        $orderBy: [QueryWorkSessionLogsOrderByOrderByClause!]
        $first: Int!
        $page: Int
    ) {
        workSessionLogs(
            text: $text
            result: $result
            where: $where
            whereUser: $whereUser
            orderBy: $orderBy
            first: $first
            page: $page
        ) {
            paginatorInfo {
                total
                count
                currentPage
                perPage
                lastPage
            }
            data {
                user_id
                ee_id
                work_session_id
                ...StaffManagementWorkSessionLogSnapshot
                relUser {
                    id
                    name
                    surname
                    subcontractor_id
                    subcontractor {
                        id
                        title
                    }
                }
                getSubcontractor {
                    id
                    title
                }
            }
        }
    }
`;
